import { DateFnsLocaleProvider } from '@if/core/date'
import { Container, CssBaseline } from '@mui/material'
import { useIsIcebergGrDevs } from '@src/providers/UserProvider'
import { useConflict, useIsAppLoading, useIsDeprecated } from '@src/data/selectors/app-config-selectors'
import { useEffect, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter as Router } from 'react-router-dom'
import AlertBanner from './AlertBanner'
import AppHeader from './AppHeader'
import ConflictDialog from './components/ConflictDialog'
import LocaleProvider from './components/LocaleProvider'
import ThemeProvider from './components/ThemeProvider'
import TopLevelRoutes from './Routes'

const App = (): JSX.Element | null => {
  const [isLoaded, setLoaded] = useState(false)
  const isIcebergGrDevs = useIsIcebergGrDevs()
  const conflict = useConflict()
  const deprecated = useIsDeprecated()
  const { t } = useTranslation()

  const hasAlertBanner = isIcebergGrDevs || (import.meta.env.PROD && deprecated)

  // App config is initialized by AppConfigProvider
  const isLoadingConfig = useIsAppLoading()

  // Mark as loaded when config is loaded (user and preferences are loaded by providers)
  useEffect(() => {
    if (!isLoadingConfig) {
      setLoaded(true)
    }
  }, [isLoadingConfig])

  if (!isLoaded) return null

  return (
    <ThemeProvider>
      <DateFnsLocaleProvider>
        <LocaleProvider>
          <CssBaseline />
          <Router>
            <AlertBanner message="Production" show={isIcebergGrDevs} />
            <AlertBanner message={t('common.deprecated')} variant="error" show={import.meta.env.PROD && deprecated} />
            <AppHeader hasAlertBanner={hasAlertBanner} />
            <Container maxWidth="xl" disableGutters>
              <TopLevelRoutes />
            </Container>
            <ConflictDialog open={conflict !== null} conflict={conflict} />
          </Router>
        </LocaleProvider>
      </DateFnsLocaleProvider>
    </ThemeProvider>
  )
}

export default App
